import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Button,
  Card,
  EmptyState,
  Inline,
  Stack,
  StatePill,
  TextInput,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { formatDateTime } from "../locale-format.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiGet, apiSend } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import { usePlatformOwner } from "../access/platform-owner.js";

/**
 * Sprint 51.0 — Owner Purge Center.
 *
 * Platform-owner-only surface for the controlled tenant hard-delete
 * flow. Two phases:
 *
 *   1. Preview — operator picks a tenant from the list; the page
 *      calls POST /v1/admin/tenants/:id/purge/preview and renders
 *      the per-table row counts + FK-blocker list.
 *
 *   2. Execute — operator types the tenant's display name as a
 *      confirmation phrase, checks the three explicit acknowledgements,
 *      waits out a 5-second countdown, and clicks the red destructive
 *      button. The page then calls POST /v1/admin/tenants/purge-jobs/
 *      :jobId/execute and surfaces the per-table deletion counts.
 *
 * Hard guarantees:
 *
 *   - Gated by `platform.owner` both server-side and in this UI.
 *   - The execute button is `disabled` until the countdown reaches
 *     0 AND all three checkboxes are checked AND the confirmation
 *     phrase matches the tenant display name (case-insensitive).
 *   - No raw SQL is sent from the UI — the backend service holds
 *     the closed table list + deletion order.
 *   - The page does not auto-execute. Even after preview, the
 *     operator must explicitly type + countdown + click.
 */

interface TenantRowDto {
  readonly id: string;
  readonly displayName: string | null;
}

interface PurgeCounts {
  readonly [table: string]: number;
}

interface PurgePreviewResponse {
  readonly jobId: string;
  readonly tenantId: string;
  readonly tenantDisplayLabel: string;
  readonly counts: PurgeCounts;
  readonly blockingTables: ReadonlyArray<string>;
  readonly previewedAt: string;
}

interface PurgeExecuteResponse {
  readonly jobId: string;
  readonly tenantId: string;
  readonly state: string;
  readonly deletionCounts: PurgeCounts;
  readonly completedAt: string;
}

const COUNTDOWN_SECONDS = 5;

interface PurgeCenterContentProps {
  readonly testId?: string;
  readonly titleKey?: string;
  readonly subtitleKey?: string;
  readonly headingLevel?: "h1" | "h2";
}

export function PurgeCenterPage() {
  const { t } = useLocale();
  const session = useSession();
  if (!usePlatformOwner(session)) {
    return (
      <Card elevation={1}>
        <EmptyState title={t("admin.purge.gate.title")} body={t("admin.purge.gate.body")} />
      </Card>
    );
  }

  return <PurgeCenterContent />;
}

/**
 * Shared Owner Purge Center workflow. Both admin surfaces render this same
 * component so preview, typed confirmation, acknowledgements, countdown and
 * execution can never drift into two implementations.
 */
export function PurgeCenterContent({
  testId = "admin-purge-page",
  titleKey = "admin.purge.title",
  subtitleKey = "admin.purge.subtitle",
  headingLevel = "h1",
}: PurgeCenterContentProps = {}) {
  const { t } = useLocale();
  const headingStyle = {
    fontSize: headingLevel === "h1" ? "var(--gt-sys-font-size-2xl)" : "var(--gt-sys-font-size-lg)",
    fontWeight: "var(--gt-sys-font-weight-bold)",
    margin: 0,
    color: "var(--gt-sys-status-danger, var(--gt-sys-text))",
  };

  return (
    <Stack gap={4} data-testid={testId}>
      <Stack gap={1}>
        {headingLevel === "h1" ? (
          <h1 style={headingStyle}>{t(titleKey)}</h1>
        ) : (
          <h2 style={headingStyle}>{t(titleKey)}</h2>
        )}
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>{t(subtitleKey)}</p>
      </Stack>
      <DangerCallout />
      <PurgeWorkflow />
    </Stack>
  );
}

function DangerCallout() {
  const { t } = useLocale();
  return (
    <Card>
      <div
        data-testid="admin-purge-danger-callout"
        style={{
          borderInlineStart: "3px solid var(--gt-sys-status-danger, #b91c1c)",
          paddingInlineStart: "var(--gt-sys-space-3)",
        }}
      >
        <Stack gap={1}>
          <strong style={{ fontSize: "var(--gt-sys-font-size-md)" }}>
            {t("admin.purge.callout.title")}
          </strong>
          <p style={{ margin: 0, fontSize: "var(--gt-sys-font-size-sm)" }}>
            {t("admin.purge.callout.body")}
          </p>
        </Stack>
      </div>
    </Card>
  );
}

function PurgeWorkflow() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const tenantsQ = useQuery<{ items: TenantRowDto[] }, Error>({
    queryKey: ["admin", "purge-tenant-picker"],
    queryFn: ({ signal }) =>
      apiGet<{ items: TenantRowDto[] }>("/v1/admin/tenants", undefined, signal),
    staleTime: 60_000,
  });

  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [preview, setPreview] = useState<PurgePreviewResponse | null>(null);
  const [result, setResult] = useState<PurgeExecuteResponse | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [ackData, setAckData] = useState(false);
  const [ackIrreversible, setAckIrreversible] = useState(false);
  const [ackTarget, setAckTarget] = useState(false);
  const [countdown, setCountdown] = useState<number>(COUNTDOWN_SECONDS);
  const [executeError, setExecuteError] = useState<string | null>(null);
  // Sprint 54.0 (Admin Console Recovery) — operator-actionable error
  // detail for the preview path. The Sprint 51.0 page rendered a
  // single generic "platform.owner missing" copy on every preview
  // failure, which the Owner correctly flagged as misleading (the
  // session DID carry platform.owner; the real failure was a
  // backend 500 caused by the `imports` table typo). The status-
  // specific copy below distinguishes 401 / 403 / 404 / 409 / 500
  // with the request id when available so the operator + support
  // get an actionable signal instead of self-blame.
  const [previewErrorDetail, setPreviewErrorDetail] = useState<{
    status: number | null;
    code: string | null;
    requestId: string | null;
    blockingTables: ReadonlyArray<string> | null;
  } | null>(null);

  const previewMutation = useMutation<PurgePreviewResponse, Error, string>({
    mutationFn: (tenantId) =>
      apiSend<PurgePreviewResponse>("POST", `/v1/admin/tenants/${tenantId}/purge/preview`),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      setConfirmation("");
      setAckData(false);
      setAckIrreversible(false);
      setAckTarget(false);
      setCountdown(COUNTDOWN_SECONDS);
      setExecuteError(null);
      setPreviewErrorDetail(null);
    },
    onError: (err) => {
      // Sprint 54.0 — capture the status + structured error code +
      // request id (and the blockingTables list when the server
      // returns 409 with data-plane blockers) so the UI can render
      // a precise, support-shareable message instead of a generic
      // "your session probably doesn't have platform.owner" blame.
      if (err instanceof ApiError) {
        type StructuredBody = {
          error?: string;
          blockingTables?: ReadonlyArray<string>;
        };
        const body = (err.body ?? null) as StructuredBody | null;
        setPreviewErrorDetail({
          status: err.status,
          code: typeof body?.error === "string" ? body.error : null,
          requestId: err.requestId ?? null,
          blockingTables: Array.isArray(body?.blockingTables) ? body.blockingTables : null,
        });
      } else {
        setPreviewErrorDetail({
          status: null,
          code: null,
          requestId: null,
          blockingTables: null,
        });
      }
    },
  });

  const executeMutation = useMutation<
    PurgeExecuteResponse,
    Error,
    { jobId: string; confirmationPhrase: string }
  >({
    mutationFn: ({ jobId, confirmationPhrase }) =>
      apiSend<PurgeExecuteResponse>("POST", `/v1/admin/tenants/purge-jobs/${jobId}/execute`, {
        confirmationPhrase,
      }),
    onSuccess: (data) => {
      setResult(data);
      setPreview(null);
      setExecuteError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
      void qc.invalidateQueries({ queryKey: ["admin", "purge-tenant-picker"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // The route returns a structured body — try to parse the
        // localized error message off the json. Fall back to the
        // status code so the operator can self-diagnose.
        // We re-fetch the body via the ApiError; in the worst case
        // the operator sees the status code.
        setExecuteError(`HTTP ${err.status}`);
      } else {
        setExecuteError(err.message);
      }
    },
  });

  // Sprint 51.0 — countdown timer. Decrements every second while a
  // preview is loaded but no execution has been kicked off. Reset
  // to COUNTDOWN_SECONDS whenever the operator opens a new preview
  // OR changes the confirmation phrase / acknowledgement state, so
  // a careless click can't sneak through. The button stays disabled
  // until ALL three conditions are true (countdown=0, phrase match,
  // every checkbox ticked).
  useEffect(() => {
    if (!preview || result) return;
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [preview, result, countdown]);

  // Any change to the confirmation phrase or acknowledgement set
  // resets the countdown — the operator must consciously wait every
  // time they edit the gate inputs.
  useEffect(() => {
    if (!preview || result) return;
    setCountdown(COUNTDOWN_SECONDS);
  }, [confirmation, ackData, ackIrreversible, ackTarget, preview, result]);

  const tenantDisplayLabel = preview?.tenantDisplayLabel ?? "";
  const confirmationMatches = useMemo(() => {
    const normalized = confirmation.trim().toLowerCase();
    if (normalized.length === 0) return false;
    if (preview && normalized === preview.tenantId.toLowerCase()) return true;
    if (tenantDisplayLabel && normalized === tenantDisplayLabel.trim().toLowerCase()) return true;
    return false;
  }, [confirmation, preview, tenantDisplayLabel]);

  const blocked = (preview?.blockingTables.length ?? 0) > 0;
  const allAcks = ackData && ackIrreversible && ackTarget;
  const executeDisabled =
    !preview ||
    blocked ||
    !confirmationMatches ||
    !allAcks ||
    countdown > 0 ||
    executeMutation.isPending;

  function onPreview(ev: FormEvent) {
    ev.preventDefault();
    if (!selectedTenantId) return;
    previewMutation.mutate(selectedTenantId);
  }

  function onExecute() {
    if (!preview || executeDisabled) return;
    setExecuteError(null);
    executeMutation.mutate({
      jobId: preview.jobId,
      confirmationPhrase: confirmation.trim(),
    });
  }

  return (
    <Stack gap={4}>
      <Card>
        <form onSubmit={onPreview}>
          <Stack gap={3}>
            <strong>{t("admin.purge.preview.title")}</strong>
            {tenantsQ.isError ? <PurgeTenantLoadError error={tenantsQ.error} /> : null}
            <Inline gap={2} align="end" wrap>
              <label style={{ display: "grid", gap: 4, minInlineSize: 320 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {t("admin.purge.preview.tenant_label")}
                </span>
                <select
                  data-testid="admin-purge-tenant-picker"
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.currentTarget.value)}
                  style={selectStyle}
                >
                  <option value="">{t("admin.purge.preview.tenant_placeholder")}</option>
                  {(tenantsQ.data?.items ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.displayName ?? t("admin.purge.preview.unnamed_tenant")} (
                      {row.id.slice(0, 8)})
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="submit"
                variant="secondary"
                disabled={!selectedTenantId || previewMutation.isPending}
                data-testid="admin-purge-preview-submit"
              >
                {previewMutation.isPending ? t("common.loading") : t("admin.purge.preview.submit")}
              </Button>
            </Inline>
            {previewMutation.isError ? <PreviewErrorBanner detail={previewErrorDetail} /> : null}
          </Stack>
        </form>
      </Card>

      {preview && !result ? (
        <Card>
          <Stack gap={3} data-testid="admin-purge-preview-panel">
            <Stack gap={1}>
              <strong style={{ fontSize: "var(--gt-sys-font-size-lg)" }}>
                {t("admin.purge.preview.summary_title", { name: preview.tenantDisplayLabel })}
              </strong>
              <span style={{ color: "var(--gt-sys-text-muted)", fontSize: 12 }}>
                {t("admin.purge.preview.tenant_id", { id: preview.tenantId })}
              </span>
            </Stack>

            {blocked ? (
              <div
                role="alert"
                data-testid="admin-purge-blocked"
                style={{
                  color: "var(--gt-sys-status-danger, #b91c1c)",
                  fontSize: 13,
                  borderInlineStart: "3px solid var(--gt-sys-status-danger, #b91c1c)",
                  paddingInlineStart: 12,
                }}
              >
                <strong>{t("admin.purge.blocked.title")}</strong>
                <ul style={{ marginBlock: 4, paddingInlineStart: 18 }}>
                  {preview.blockingTables.map((table) => (
                    <li key={table}>
                      <code>{table}</code>
                    </li>
                  ))}
                </ul>
                <span>{t("admin.purge.blocked.body")}</span>
              </div>
            ) : null}

            <CountsTable counts={preview.counts} />

            <Stack gap={2}>
              <strong>{t("admin.purge.confirm.title")}</strong>
              <p style={{ margin: 0, fontSize: 13, color: "var(--gt-sys-text-muted)" }}>
                {t("admin.purge.confirm.body")}
              </p>
              <Inline gap={2} wrap>
                <label style={ackLabelStyle} data-testid="admin-purge-ack-data">
                  <input
                    type="checkbox"
                    checked={ackData}
                    onChange={(e) => setAckData(e.currentTarget.checked)}
                  />
                  <span>{t("admin.purge.ack.data")}</span>
                </label>
                <label style={ackLabelStyle} data-testid="admin-purge-ack-irreversible">
                  <input
                    type="checkbox"
                    checked={ackIrreversible}
                    onChange={(e) => setAckIrreversible(e.currentTarget.checked)}
                  />
                  <span>{t("admin.purge.ack.irreversible")}</span>
                </label>
                <label style={ackLabelStyle} data-testid="admin-purge-ack-target">
                  <input
                    type="checkbox"
                    checked={ackTarget}
                    onChange={(e) => setAckTarget(e.currentTarget.checked)}
                  />
                  <span>{t("admin.purge.ack.target")}</span>
                </label>
              </Inline>
              <TextInput
                label={t("admin.purge.confirm.phrase_label", { name: preview.tenantDisplayLabel })}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                data-testid="admin-purge-confirm-phrase"
              />
              {confirmation && !confirmationMatches ? (
                <span
                  role="alert"
                  data-testid="admin-purge-confirm-mismatch"
                  style={{ color: "var(--gt-sys-danger)", fontSize: 12 }}
                >
                  {t("admin.purge.confirm.mismatch_hint")}
                </span>
              ) : null}

              <Inline gap={2} align="center">
                <Button
                  type="button"
                  variant="danger"
                  disabled={executeDisabled}
                  onClick={onExecute}
                  data-testid="admin-purge-execute-button"
                >
                  {executeMutation.isPending
                    ? t("admin.purge.execute.in_progress")
                    : countdown > 0
                      ? t("admin.purge.execute.countdown", { seconds: countdown })
                      : t("admin.purge.execute.submit")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setPreview(null)}
                  data-testid="admin-purge-cancel"
                >
                  {t("common.cancel")}
                </Button>
                {executeError ? (
                  <span
                    role="alert"
                    data-testid="admin-purge-execute-error"
                    style={{ color: "var(--gt-sys-danger)", fontSize: 13 }}
                  >
                    {executeError}
                  </span>
                ) : null}
              </Inline>
            </Stack>
          </Stack>
        </Card>
      ) : null}

      {result ? (
        <Card>
          <Stack gap={2} data-testid="admin-purge-result-panel">
            <Inline gap={2} align="center">
              <StatePill tone="success" label={result.state} />
              <strong>{t("admin.purge.result.title")}</strong>
            </Inline>
            <p style={{ margin: 0, fontSize: 13 }}>
              {t("admin.purge.result.body", { id: result.tenantId, at: result.completedAt })}
            </p>
            <CountsTable counts={result.deletionCounts} />
            <Inline gap={2}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setResult(null);
                  setSelectedTenantId("");
                }}
                data-testid="admin-purge-result-dismiss"
              >
                {t("common.clear")}
              </Button>
            </Inline>
          </Stack>
        </Card>
      ) : null}

      <PurgeJobHistory />
    </Stack>
  );
}

function PurgeTenantLoadError({ error }: { readonly error: Error }) {
  const { t } = useLocale();
  const status = error instanceof ApiError ? error.status : null;
  const title =
    status === 403
      ? t("admin.purge.preview.error.403_title")
      : status === 500
        ? t("admin.purge.preview.error.500_title")
        : t("admin.purge.preview.error.generic_title");
  const body =
    status === 403
      ? t("admin.purge.preview.error.403_body")
      : status === 500
        ? t("admin.purge.preview.error.500_body")
        : t("admin.purge.preview.error.generic_body");

  return (
    <div role="alert" data-testid="admin-purge-tenant-load-error">
      <Stack gap={1}>
        <strong>{title}</strong>
        <span>{body}</span>
      </Stack>
    </div>
  );
}

function CountsTable({ counts }: { counts: PurgeCounts }) {
  const { t } = useLocale();
  const rows = useMemo(
    () =>
      Object.entries(counts)
        .filter(([, n]) => typeof n === "number")
        .sort(([a], [b]) => a.localeCompare(b)),
    [counts],
  );
  if (rows.length === 0) {
    return (
      <div style={{ color: "var(--gt-sys-text-muted)", fontSize: 13 }}>
        {t("admin.purge.counts.empty")}
      </div>
    );
  }
  return (
    <table
      data-testid="admin-purge-counts-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "var(--gt-sys-font-size-sm)",
      }}
    >
      <thead>
        <tr style={{ color: "var(--gt-sys-text-muted)", textAlign: "start" }}>
          <th scope="col" style={{ padding: "6px 8px", textAlign: "start" }}>
            {t("admin.purge.counts.col.table")}
          </th>
          <th scope="col" style={{ padding: "6px 8px", textAlign: "end" }}>
            {t("admin.purge.counts.col.count")}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([table, n]) => (
          <tr key={table} style={{ borderBlockStart: "1px solid var(--gt-sys-border)" }}>
            <td style={{ padding: "6px 8px" }}>
              <code>{table}</code>
            </td>
            <td
              style={{ padding: "6px 8px", textAlign: "end", fontVariantNumeric: "tabular-nums" }}
            >
              {n}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface PurgeJobDto {
  readonly id: string;
  readonly tenantId: string;
  readonly tenantDisplayLabel: string;
  readonly state: string;
  readonly actorEmail: string;
  readonly previewedAt: string;
  readonly completedAt: string | null;
  readonly failureReason: string | null;
}

function PurgeJobHistory() {
  const { t } = useLocale();
  const q = useQuery<{ items: PurgeJobDto[] }, Error>({
    queryKey: ["admin", "purge-jobs"],
    queryFn: ({ signal }) =>
      apiGet<{ items: PurgeJobDto[] }>("/v1/admin/tenants/purge-jobs", undefined, signal),
    staleTime: 15_000,
  });
  if (q.isLoading || !q.data) return null;
  const items = q.data.items ?? [];
  if (items.length === 0) return null;
  return (
    <Card>
      <Stack gap={2}>
        <strong>{t("admin.purge.history.title")}</strong>
        <table
          data-testid="admin-purge-history-table"
          aria-label={t("admin.purge.history.title")}
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "var(--gt-sys-font-size-sm)",
          }}
        >
          <thead>
            <tr style={{ color: "var(--gt-sys-text-muted)", textAlign: "start" }}>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "start" }}>
                {t("admin.purge.history.col.tenant")}
              </th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "start" }}>
                {t("admin.purge.history.col.state")}
              </th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "start" }}>
                {t("admin.purge.history.col.actor")}
              </th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "start" }}>
                {t("admin.purge.history.col.previewed_at")}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((job) => (
              <tr key={job.id} style={{ borderBlockStart: "1px solid var(--gt-sys-border)" }}>
                <td style={{ padding: "6px 8px" }}>{job.tenantDisplayLabel}</td>
                <td style={{ padding: "6px 8px" }}>
                  <code>{job.state}</code>
                </td>
                <td style={{ padding: "6px 8px" }}>{job.actorEmail}</td>
                <td style={{ padding: "6px 8px" }}>{formatDateTime(job.previewedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Stack>
    </Card>
  );
}

const selectStyle = {
  fontSize: 14,
  paddingBlock: 8,
  paddingInline: 10,
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
} as const;

const ackLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: "var(--gt-sys-font-size-sm)",
} as const;

/**
 * Sprint 54.0 (Admin Console Recovery) — preview-error banner with
 * status-specific copy + the API request id when available. Replaces
 * the Sprint 51.0 single-string banner that always said
 * "Vorschau fehlgeschlagen. Prüfen Sie, ob der Tenant existiert
 * und Ihre Sitzung weiterhin platform.owner trägt." even when the
 * actual failure was a 500 backend bug (the Owner correctly flagged
 * this as misleading self-blame).
 *
 * The banner maps:
 *   - 401 / 403 → session / role missing
 *   - 404 → tenant not found
 *   - 409 + blockingTables → data-plane blocker list
 *   - 409 (other) → generic conflict
 *   - 500 → support-safe with request id
 *   - everything else → fallback with request id
 */
function PreviewErrorBanner({
  detail,
}: {
  detail: {
    status: number | null;
    code: string | null;
    requestId: string | null;
    blockingTables: ReadonlyArray<string> | null;
  } | null;
}) {
  const { t } = useLocale();
  const status = detail?.status ?? null;
  const code = detail?.code ?? null;
  const requestId = detail?.requestId ?? null;
  const blockingTables = detail?.blockingTables ?? null;

  const wrapStyle = {
    color: "var(--gt-sys-status-danger, #b91c1c)",
    fontSize: 13,
    borderInlineStart: "3px solid var(--gt-sys-status-danger, #b91c1c)",
    paddingInlineStart: 12,
    paddingBlock: 8,
  } as const;

  // Owner-facing copy by status. The 401/403 message intentionally
  // does NOT blame the operator's session unless we actually got a
  // 401/403 from the server.
  let titleKey: string;
  let bodyKey: string | null;
  let listBlockers = false;
  if (status === 401) {
    titleKey = "admin.purge.preview.error.401_title";
    bodyKey = "admin.purge.preview.error.401_body";
  } else if (status === 403) {
    titleKey = "admin.purge.preview.error.403_title";
    bodyKey = "admin.purge.preview.error.403_body";
  } else if (status === 404 || code === "tenant_not_found") {
    titleKey = "admin.purge.preview.error.404_title";
    bodyKey = "admin.purge.preview.error.404_body";
  } else if (status === 409 && blockingTables !== null && blockingTables.length > 0) {
    titleKey = "admin.purge.preview.error.blocked_title";
    bodyKey = "admin.purge.preview.error.blocked_body";
    listBlockers = true;
  } else if (status === 409) {
    titleKey = "admin.purge.preview.error.409_title";
    bodyKey = "admin.purge.preview.error.409_body";
  } else if (status !== null && status >= 500) {
    titleKey = "admin.purge.preview.error.500_title";
    bodyKey = "admin.purge.preview.error.500_body";
  } else {
    titleKey = "admin.purge.preview.error.generic_title";
    bodyKey = "admin.purge.preview.error.generic_body";
  }

  return (
    <div role="alert" data-testid="admin-purge-preview-error-banner" style={wrapStyle}>
      <Stack gap={1}>
        <strong data-testid="admin-purge-preview-error-title">{t(titleKey)}</strong>
        {bodyKey ? <span data-testid="admin-purge-preview-error-body">{t(bodyKey)}</span> : null}
        {listBlockers && blockingTables ? (
          <ul
            data-testid="admin-purge-preview-error-blockers"
            style={{ marginBlock: 4, paddingInlineStart: 18 }}
          >
            {blockingTables.map((table) => (
              <li key={table}>
                <code>{table}</code>
              </li>
            ))}
          </ul>
        ) : null}
        {status !== null || requestId ? (
          <span
            data-testid="admin-purge-preview-error-meta"
            style={{ color: "var(--gt-sys-text-muted)", fontSize: 12 }}
          >
            {status !== null ? t("admin.purge.preview.error.meta_status", { status }) : null}
            {status !== null && requestId ? " · " : null}
            {requestId ? t("admin.purge.preview.error.meta_request_id", { requestId }) : null}
          </span>
        ) : null}
      </Stack>
    </div>
  );
}
